import React, { useEffect, useState } from 'react';
import { ListChecks, Plus, Check, Trash2, Loader2, Clock } from 'lucide-react';
import SheetOrPage from './SheetOrPage';
import { listGroupNotes, addGroupNote, resolveGroupNote, type GroupNote } from '../../services/conversationService';
import { TYPE } from '../../constants/typeScale';

/**
 * GroupNotesPanel (A2) — a THIN per-group "requests" list. The owner jots a request; once a day the best expertise-
 * matched crew agent picks up the oldest open one and posts a finding back in the chat. This panel = add a request,
 * see the list (open / done), tick one off or remove it. Owner-only (the routes enforce it). Monochrome + Lucide.
 */
interface Props { open: boolean; conversationId: string | null; asPage?: boolean; onClose: () => void }

const STATUS: Record<GroupNote['status'], { label: string; cls: string }> = {
  open: { label: 'Waiting for pickup', cls: 'text-secondary-400' },
  picked: { label: 'A specialist is on it', cls: 'text-secondary-500' },
  done: { label: 'Done', cls: 'text-emerald-600 dark:text-emerald-500' },
};

const GroupNotesPanel: React.FC<Props> = ({ open, conversationId, asPage, onClose }) => {
  const [notes, setNotes] = useState<GroupNote[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !conversationId) return;
    setNotes(null); setError('');
    listGroupNotes(conversationId).then(setNotes).catch(() => setError('Could not load requests.'));
  }, [open, conversationId]);

  const add = async () => {
    const text = draft.trim();
    if (!text || !conversationId) return;
    setBusy(true); setError('');
    try { setNotes(await addGroupNote(conversationId, text)); setDraft(''); }
    catch { setError('Could not add that request.'); }
    finally { setBusy(false); }
  };

  const resolve = async (noteId: string, remove: boolean) => {
    if (!conversationId) return;
    try { setNotes(await resolveGroupNote(conversationId, noteId, remove)); }
    catch { setError('Could not update that request.'); }
  };

  return (
    <SheetOrPage open={open} asPage={asPage} title="Requests for your crew" icon={<ListChecks className="w-4 h-4" />} onClose={onClose}>
      <p className={`mb-3 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>
        Jot something for your crew to look into. Once a day the right specialist picks up a request and reports back in the chat.
      </p>

      <div className="mb-4 flex items-start gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); add(); } }}
          rows={2}
          placeholder="e.g. tighten the opening hook of episode 2"
          className={`min-w-0 flex-1 resize-none rounded-lg border border-secondary-300 bg-white px-3 py-2 ${TYPE.body} focus:border-secondary-500 focus:outline-none dark:border-secondary-600 dark:bg-secondary-900`}
        />
        <button type="button" onClick={add} disabled={busy || !draft.trim()}
          className={`inline-flex items-center gap-1 rounded-lg bg-secondary-900 px-3 py-2 ${TYPE.body} font-medium text-white disabled:opacity-40 dark:bg-white dark:text-secondary-900`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
        </button>
      </div>

      {error && <p className={`mb-2 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>{error}</p>}

      {notes === null ? (
        <div className={`flex items-center gap-2 py-4 ${TYPE.body} text-secondary-400`}><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : notes.length === 0 ? (
        <p className={`rounded-xl border border-dashed border-secondary-200 py-5 text-center ${TYPE.control} text-secondary-400 dark:border-secondary-600`}>No requests yet — add one above.</p>
      ) : (
        <ul className="space-y-1.5">
          {notes.map((n) => {
            const s = STATUS[n.status];
            const done = n.status === 'done';
            return (
              <li key={n.id} className="flex items-start gap-2 rounded-xl border border-secondary-200 p-2.5 dark:border-secondary-600">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-secondary-300 dark:text-secondary-600" />
                <div className="min-w-0 flex-1">
                  <p className={`${TYPE.body} ${done ? 'text-secondary-400 line-through' : 'text-secondary-900 dark:text-white'}`}>{n.text}</p>
                  <p className={`mt-0.5 ${TYPE.meta} ${s.cls}`}>{s.label}</p>
                </div>
                {!done && <button type="button" onClick={() => resolve(n.id, false)} aria-label="Mark done" className="rounded-md p-1 text-secondary-400 hover:text-emerald-600"><Check className="h-4 w-4" /></button>}
                <button type="button" onClick={() => resolve(n.id, true)} aria-label="Remove" className="rounded-md p-1 text-secondary-400 hover:text-secondary-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </SheetOrPage>
  );
};

export default GroupNotesPanel;
